import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { Lesson } from '@/types';

interface NoteData {
  content: string;
  lastModified: Date;
}

/**
 * Downloads lesson notes as a PDF file
 * @param lessonId - The lesson ID to get notes for
 * @param date - The date to include in the filename
 */
export const downloadLessonNotesAsPDF = async (lessonId: string, date: string, lesson?: Lesson): Promise<void> => {
  try {
    // Get the lesson notes from localStorage
    const storageKey = `lessonNote_${lessonId}`;
    const savedNote = localStorage.getItem(storageKey);
    
    if (!savedNote) {
      return;
    }

    const noteData: NoteData = JSON.parse(savedNote);
    
    // Check if the note content is not empty (including empty HTML tags)
    if (!noteData.content || noteData.content.trim() === '' || noteData.content.trim() === '<p></p>' || noteData.content.replace(/<[^>]*>/g, '').trim() === '') {
      return;
    }

    // Create a temporary div to render the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = noteData.content;
    
    // Apply inline styles to override any global CSS and avoid OKLCH colors
    tempDiv.style.cssText = `
      position: absolute !important;
      left: -9999px !important;
      top: 0 !important;
      width: 800px !important;
      padding: 20px !important;
      font-family: Arial, sans-serif !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
      color: #000000 !important;
      background-color: #ffffff !important;
      border: none !important;
      margin: 0 !important;
      box-shadow: none !important;
      text-shadow: none !important;
      filter: none !important;
      backdrop-filter: none !important;
    `;
    
    // Add some basic styling to preserve formatting with explicit colors
    const style = document.createElement('style');
    style.textContent = `
      .temp-pdf-content {
        all: initial !important;
        font-family: Arial, sans-serif !important;
        font-size: 14px !important;
        line-height: 1.6 !important;
        color: #000000 !important;
        background-color: #ffffff !important;
      }
      .temp-pdf-content h1, .temp-pdf-content h2, .temp-pdf-content h3 {
        margin-top: 20px !important;
        margin-bottom: 10px !important;
        font-weight: bold !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
      .temp-pdf-content h1 { font-size: 24px !important; }
      .temp-pdf-content h2 { font-size: 20px !important; }
      .temp-pdf-content h3 { font-size: 16px !important; }
      .temp-pdf-content p { 
        margin-bottom: 10px !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
      .temp-pdf-content ul, .temp-pdf-content ol { 
        margin-bottom: 10px !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
      .temp-pdf-content li { 
        margin-bottom: 5px !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
      .temp-pdf-content strong { 
        font-weight: bold !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
      .temp-pdf-content em { 
        font-style: italic !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
      .temp-pdf-content u { 
        text-decoration: underline !important;
        color: #000000 !important;
        background-color: transparent !important;
      }
    `;
    
    tempDiv.className = 'temp-pdf-content';
    document.head.appendChild(style);
    document.body.appendChild(tempDiv);

    // Wait a bit for the content to render
    await new Promise(resolve => setTimeout(resolve, 100));

    // Convert the HTML content to canvas with robust configuration
    const canvas = await html2canvas(tempDiv, {
      width: 800,
      height: tempDiv.scrollHeight,
      scale: 2, // Higher resolution
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff', // Ensure white background
      logging: false, // Disable logging to reduce console noise
      ignoreElements: (element) => {
        // Ignore elements that might cause issues
        return element.classList.contains('ignore-pdf') || 
               element.tagName === 'SCRIPT' || 
               element.tagName === 'STYLE';
      },
      onclone: (clonedDoc) => {
        // Additional processing on the cloned document
        const clonedElement = clonedDoc.querySelector('.temp-pdf-content') as HTMLElement;
        if (clonedElement) {
          // Ensure no CSS variables are inherited
          clonedElement.style.setProperty('color', '#000000', 'important');
          clonedElement.style.setProperty('background-color', '#ffffff', 'important');
        }
      }
    });

    // Clean up the temporary elements
    document.body.removeChild(tempDiv);
    document.head.removeChild(style);

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 10;

    // Add header
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Tutred Lesson Notes', pdfWidth / 2, 20, { align: 'center' });
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Date: ${date}`, pdfWidth / 2, 30, { align: 'center' });
    
    pdf.setFontSize(10);
    pdf.text(`Lesson taught by: ${lesson?.tutorName}`, pdfWidth / 2, 35, { align: 'center' });

    // Add the content image
    pdf.addImage(imgData, 'PNG', imgX, imgY + 30, imgWidth * ratio, imgHeight * ratio);

    // Generate filename
    const filename = `${date} Tutred Lesson Notes.pdf`;
    
    // Download the PDF
    pdf.save(filename);
  } catch (error) {
    console.error('Error downloading PDF:', error);
  }
};

/**
 * Formats a date for use in the PDF filename
 * @param date - Date object or string
 * @returns Formatted date string (YYYY-MM-DD)
 */
export const formatDateForFilename = (date: Date | string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
